<template>
    <div class="editor-container">

        <!-- Toolbar -->
        <div v-if="localLayout.dataset.type !== 'MULTICLASS' && localLayout.dataset.type !== 'ANOMALY'" class="editor-toolbar" :class="{ 'toolbar-expanded': isExpanded }">
            <div class="toolbar-main">
                <div class="toolbar-buttons">

                    <button class="toolbar-btn" 
                        :class="{ active: currentTool === 'panning' }"
                        @click="setupTool('panning')"
                        title="Position Mode">
                        <img :src="require(`@/assets/images/rosepetal/icon/hand.png`)" class="w-5 h-5" />
                    </button>

                    <template v-if="localLayout.dataset.type === 'MULTILABEL'">
                        <div class="relative">
                            <button class="toolbar-btn" 
                                :class="{ active: currentTool === 'eraser' }"
                                @click="setupTool('eraser')"
                                title="Erase">
                                <img :src="require(`@/assets/images/rosepetal/icon/erase.png`)" class="w-5 h-5" />
                            </button>
                            
                            <div class="tool-panel" v-if="currentTool === 'eraser'">
                                <div class="slider-header">
                                    <label>{{ $t('Brush size') }}</label>
                                    <span class="slider-value">{{ localLayout.canvas.tools.modalBrush.size }}px</span>
                                </div>
                                <Slider @update:modelValue="handleBrushSizeChange"
                                    v-model="localLayout.canvas.tools.modalBrush.size" :min="1" :max="120" :step="1"
                                    :tooltip="false" :tooltips="false" class="slider-blue" />
                            </div>
                        </div>

                        <div class="relative">
                            <button class="toolbar-btn" 
                                :class="{ active: currentTool === 'brush' }"
                                @click="setupTool('brush')"
                                title="Draw">
                                <img :src="require(`@/assets/images/rosepetal/icon/pencil.png`)" class="w-5 h-5" />
                            </button>
                            
                            <div class="tool-panel" v-if="currentTool === 'brush'">
                                <div class="slider-header">
                                    <label>{{ $t('Brush size') }}</label>
                                    <span class="slider-value">{{ localLayout.canvas.tools.modalBrush.size }}px</span>
                                </div>
                                <Slider @update:modelValue="handleBrushSizeChange"
                                    v-model="localLayout.canvas.tools.modalBrush.size" :min="1" :max="120" :step="1"
                                    :tooltip="false" :tooltips="false" class="slider-blue" />
                            </div>
                        </div>

                        <div class="relative">
                            <button class="toolbar-btn" 
                                :class="{ active: currentTool === 'polygon' }"
                                @click="setupTool('polygon')"
                                title="Polygon">
                                <GitCommitIcon class="w-5 h-5" />
                            </button>
                        </div>
                    </template>

                    <template v-if="localLayout.dataset.type === 'imageObjectDetection'">
                        <button class="toolbar-btn" 
                            :class="{ active: currentTool === 'moveBB' }"
                            @click="setupTool('moveBB')"
                            title="Move Bounding Box">
                            <MoveIcon class="w-5 h-5" />
                        </button>
                        <div class="relative">
                            <button class="toolbar-btn" 
                                :class="{ active: currentTool === 'drawBB' }"
                                @click="setupTool('drawBB')"
                                title="Draw Bounding Box">
                                <img :src="require(`@/assets/images/rosepetal/icon/bounding.png`)" class="w-5 h-5" />
                            </button>
                        </div>
                    </template>

                    <template v-if="props.enableSAM && (localLayout.dataset.type === 'imageObjectDetection' || localLayout.dataset.type === 'MULTILABEL')">

                        <div class="sam-tools-container" 
                            :class="{'sam-active': currentTool === 'samDetection'}"
                            @mouseenter="handleSamMouseEnter"
                            @mouseleave="handleSamMouseLeave">
                            
                            <button class="toolbar-btn" 
                                :class="{ active: currentTool === 'samDetection' }"
                                @click="setupTool('samDetection')"
                                :disabled="localLayout.canvas.tools.modalDetection.loading">
                                <StarIcon class="w-5 h-5" />
                            </button>

                            <button class="toolbar-btn"
                                @click="emit('setPredictedMask')" :disabled="!SAMActive">
                                <PlusSquareIcon class="w-5 h-5" />
                            </button>
                            
                            <div class="tool-panel sam-panel" 
                                v-if="currentTool === 'samDetection' && isPanelVisible"
                                :class="{ 'sam-loading': !SAMActive }"
                                @mouseenter="SAMActive ? handleSamMouseEnter : null"
                                @mouseleave="SAMActive ? handleSamMouseLeave : null">
                                
                                <div v-if="!SAMActive" class="space-y-6">
                                    <div class="slider-header">
                                        <label>Smart Labeling</label>
                                    </div>
                                    <div class="flex items-center justify-between">
                                        <div class="flex items-center space-x-3">
                                            <div v-if="!props.samSteps.instancePrepared" class="w-5 h-5 border-4 border-gray-300 rounded-full animate-spin"></div>
                                            <div v-else class="text-green-500 font-bold">✔</div>
                                            <span class="font-medium" :class="props.samSteps.instancePrepared ? 'text-green-600' : 'text-gray-800'">
                                                Starting Instance
                                            </span>
                                        </div>
                                        <div class="text-sm" :class="props.samSteps.instancePrepared ? 'text-green-600' : 'text-gray-600'">
                                            {{ props.samSteps.instancePreparedStatus || 'pending' }}
                                            <small v-if="props.samSteps.instancePrepared">({{ props.samSteps.instancePrepared }} ms)</small>
                                        </div>
                                    </div>

                                    <div class="flex items-center justify-between">
                                        <div class="flex items-center space-x-3">
                                            <div v-if="!props.samSteps.modelReady" class="w-5 h-5 border-4 border-gray-300 rounded-full animate-spin"></div>
                                            <div v-else class="text-green-500 font-bold">✔</div>
                                            <span class="font-medium" :class="props.samSteps.modelReady ? 'text-green-600' : 'text-gray-800'">
                                                Loading Model
                                            </span>
                                        </div>
                                        <div class="text-sm" :class="props.samSteps.modelReady ? 'text-green-600' : 'text-gray-600'">
                                            {{ props.samSteps.modelReadyStatus || 'pending' }}
                                            <small v-if="props.samSteps.modelReady">({{ props.samSteps.modelReady }} ms)</small>
                                        </div>
                                    </div>
                                </div>

                                <div v-else>
                                    <div class="slider-header">
                                        <label>Smart Labeling</label>
                                        <button class="sam-help-btn" @click="emit('showSamHelp')" title="Smart Labeling tutorial">
                                            <HelpCircleIcon class="w-4 h-4" />
                                        </button>
                                    </div>

                                    <div class="sam-instruction-row">
                                        <span class="instruction-text">Add point</span>
                                        <div class="instruction-keys">
                                            <kbd>Click</kbd>
                                        </div>
                                    </div>

                                    <div class="sam-instruction-row">
                                        <span class="instruction-text">Add area</span>
                                        <div class="instruction-keys">
                                            <kbd>Click</kbd> <span class="plus-text">+</span> <kbd>Drag</kbd>
                                        </div>
                                    </div>

                                    <div class="sam-instruction-row">
                                        <span class="instruction-text">Remove</span>
                                        <div class="instruction-keys">
                                            <kbd>Ctrl</kbd> <span class="plus-text">+</span> <kbd>Click</kbd>
                                        </div>
                                    </div>

                                    <div class="sam-divider"></div>

                                    <div class="sam-instruction-row">
                                        <span class="instruction-text">Confirm section</span>
                                        <div class="instruction-keys">
                                            <kbd>Tab</kbd> <span class="or-text">or</span> <PlusSquareIcon class="w-5 h-5 inline-icon" />
                                        </div>
                                    </div>
                                </div>

                            </div>

                        </div>

                    </template>

                    <!-- VLM Tools -->
                    <template v-if="(localLayout.dataset.type === 'imageObjectDetection' || localLayout.dataset.type === 'MULTILABEL') && props.enableVLM">
                        <button class="toolbar-btn"
                            :class="{ active: currentTool === 'vlmDetection' }"
                            @click="setupTool('vlmDetection')" title="Auto Labeling">
                            <img :src="require(`@/assets/images/rosepetal/icon/vlm.png`)" class="w-5 h-5" />
                        </button>
                    </template>

                </div>
            </div>
        </div>

        <!-- Available Tags -->
        <div v-if="showAvailableTags"
            ref="availableTagsPanel"
            class="draggable-tags-panel"
            :style="availableTagsPosition"
            @mousedown="startDragging('tags', $event)">
            
            <div class="panel-header">
                <h3 class="panel-title">{{ $t('Available tags') }}</h3>
                <button class="drag-handle" title="Drag panel">
                    <MoveIcon class="w-4 h-4" />
                </button>
            </div>

            <div class="add-tag-section">
                <button class="action-button primary" 
                    @click="emit('openCreateTag')"
                    title="Create new tag">
                    <PlusIcon class="w-4 h-4 mr-1" /> {{ $t('New tag type') }}
                </button>
            </div>

            <div class="tags-list">
                <div v-for="tag in tagCounts" 
                    :key="tag.id" 
                    class="tag-item"
                    :class="{ active: isTagActive(tag) }"
                    @click="selectTag(tag)">
                    <div class="tag-color" :style="{ backgroundColor: tag.color }"></div>
                    <div class="tag-name">{{ tag.name }}</div>
                    <div class="tag-count">{{ tag.count }}</div>
                </div>
                
                <div v-if="tagCounts.length === 0" class="no-tags-message">
                    {{ $t('No tags available') }}
                </div>
            </div>
        </div>

        <!-- VLM Tools -->
        <div v-if="currentTool === 'vlmDetection' && props.enableVLM" ref="vlmPanel" class="draggable-tags-panel vlm-panel" :style="[vlmPanelPosition, { width: '420px' }]"
            @mousedown="startDragging('vlm', $event)">
            
            <div class="panel-header">
                <h3 class="panel-title">{{ $t('Auto Labeling') }}</h3>
                <button class="drag-handle" title="Minimize Panel" @click="setupTool('panning')">
                    <MinusIcon class="w-4 h-4" />
                </button>
            </div>

            <div class="relative">

                <!-- VLM selection -->
                <div class="vlm-selectors flex items-center space-x-4 p-4" :class="{ 'disabled-section': processing }">
                    <select v-model="vlmMode"
                        class="vlm-mode-select appearance-none bg-gray-200 border border-gray-300 rounded-lg px-3 py-2 text-base w-40 focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="generic">Generic</option>
                        <option value="fineTuned">Fine-Tuned</option>
                    </select>
                    <select v-model="vlmOption"
                        class="vlm-option-select appearance-none bg-gray-200 border border-gray-300 rounded-lg px-3 py-2 text-base w-64 focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option v-for="option in vlmOptions" :key="option.id" :value="option">
                            {{ option.name || option.id }}
                        </option>
                    </select>
                </div>

                <!-- Custom Prompt/Tag Section -->
                <div class="vlm-custom-prompts p-4 border-t" :class="{ 'disabled-section': processing }">
                    <div class="tags-list">
                        <div v-for="(item, index) in customPrompts" :key="index"
                            class="custom-prompt-item border rounded mb-2 p-2" style="cursor: pointer;">
                            <div v-if="editingIndex === index">
                                <select v-model="customPrompts[index].tag" @change="onEditTagChange(index)" class="mb-2 px-2 py-1 border rounded w-full"
                                    @click.stop>
                                    <option v-for="tag in tagCounts" :key="tag.id" :value="tag">
                                        {{ tag.name }}
                                    </option>
                                </select>
                                <input type="text" v-model="customPrompts[index].prompt" placeholder="Write prompt..."
                                    class="mb-2 px-2 py-1 border rounded w-full" @click.stop />
                                <div class="mt-2 flex justify-end space-x-2">
                                    <button @click.stop="applyEdit(index)" class="rp-btn-action-primary">
                                        Apply
                                    </button>
                                    <button @click.stop="deletePrompt(index)" class="rp-btn-action-danger">
                                        Delete
                                    </button>
                                </div>
                            </div>
                            <div v-else class="flex items-center">
                                <div @click="expandPrompt(index)"
                                    class="flex-1 overflow-hidden whitespace-nowrap text-ellipsis pr-2">
                                    <span class="tag-item.active"><strong>{{ item.tag.name }}</strong></span>
                                    <span class="custom-prompt-text">: {{ item.prompt }}</span>
                                </div>
                                <button @click.stop="deletePrompt(index)" class="text-red-500 flex-shrink-0 ml-2">
                                    <XIcon class="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        <div v-if="showNewPrompt" class="new-prompt flex flex-col p-2 border rounded mb-2">
                            <select v-model="newTag"  @change="onNewTagChange" class="mb-2 px-2 py-1 border rounded">
                                <option v-for="tag in tagCounts" :key="tag.id" :value="tag.name">
                                    {{ tag.name }}
                                </option>
                            </select>
                            <input type="text" v-model="newPromptText" placeholder="Write prompt..."
                                class="mb-2 px-2 py-1 border rounded" />
                            <button @click="applyNewPrompt" class="rp-btn-action-primary">
                                Apply
                            </button>
                        </div>
                    </div>
                    <div class="flex flex-col mt-4 space-y-4">
                        <button @click="toggleNewPrompt" class="rp-btn-action-secondary">
                            <PlusIcon class="w-4 h-4" />
                        </button>
                        <div class="border-t border-gray-300 my-2"></div>
                        <div class="flex items-center justify-between w-full">
                            <button @click="emitVlmData" class="flex items-center justify-center px-4 py-2 rp-btn-primary">
                                {{ $t('Infer') }}
                            </button>
                            <div class="flex items-center">
                                <span class="mr-2 text-sm">{{ $t('Auto') }}</span>
                                <label class="switch">
                                    <input type="checkbox" v-model="autoToggle" />
                                    <span class="slider round"></span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- OVERLAY DE CARGA -->
                <div v-if="processing && !VLMActive" class="absolute inset-0 bg-white bg-opacity-90 z-40 flex justify-start">
                    <div class="bg-white p-6 w-full max-w-md space-y-6">

                        <!-- STEP 1 -->
                        <div class="flex items-center justify-between">
                            <div class="flex items-center space-x-3">
                                <!-- icono -->
                                <div v-if="!stepTimes.instancePrepared"
                                    class="w-5 h-5 border-4 border-gray-300 rounded-full animate-spin"></div>
                                <div v-else class="text-green-500 font-bold">✔</div>

                                <!-- texto -->
                                <span
                                class="font-medium"
                                :class="stepTimes.instancePrepared ? 'text-green-600' : 'text-gray-800'"
                                >
                                Starting Instance
                                </span>
                            </div>

                            <!-- estado -->
                            <div
                                class="text-sm"
                                :class="stepTimes.instancePrepared ? 'text-green-600' : 'text-gray-600'"
                            >
                                {{ stepTimes.instancePreparedStatus || 'pending' }}
                                <small v-if="stepTimes.instancePrepared">
                                ({{ stepTimes.instancePrepared }} ms)
                                </small>
                            </div>
                        </div>

                        <!-- STEP 2 -->
                        <div class="flex items-center justify-between">
                            <div class="flex items-center space-x-3">
                                <div v-if="stepTimes.instanceLoaded == null || !stepTimes.instanceLoaded"
                                    class="w-5 h-5 border-4 border-gray-300 rounded-full animate-spin"></div>
                                <div v-else class="text-green-500 font-bold">✔</div>

                                <span
                                class="font-medium"
                                :class="stepTimes.instanceLoaded ? 'text-green-600' : 'text-gray-800'"
                                >
                                Loading Instance
                                </span>
                            </div>

                            <div
                                class="text-sm"
                                :class="stepTimes.instanceLoaded ? 'text-green-600' : 'text-gray-600'"
                            >
                                {{ stepTimes.instanceLoadedStatus || 'pending' }}
                                <small v-if="stepTimes.instanceLoaded">
                                ({{ stepTimes.instanceLoaded }} ms)
                                </small>
                            </div>
                        </div>

                        <!-- STEP 3 -->
                        <div class="flex items-center justify-between">
                            <div class="flex items-center space-x-3">
                                <div v-if="!stepTimes.modelReady"
                                    class="w-5 h-5 border-4 border-gray-300 rounded-full animate-spin"></div>
                                <div v-else class="text-green-500 font-bold">✔</div>

                                <span
                                class="font-medium"
                                :class="stepTimes.modelReady ? 'text-green-600' : 'text-gray-800'"
                                >
                                Downloading Model
                                </span>
                            </div>

                            <div
                                class="text-sm"
                                :class="stepTimes.modelReady ? 'text-green-600' : 'text-gray-600'"
                            >
                                {{ stepTimes.modelReadyStatus || 'pending' }}
                                <small v-if="stepTimes.modelReady">
                                ({{ stepTimes.modelReady }} ms)
                                </small>
                            </div>
                        </div>

                        <!-- (SAM) -->
                        <div v-if="props.layout.dataset.type === 'MULTILABEL'" class="flex items-center justify-between">
                            <div class="flex items-center space-x-3">
                                <div v-if="stepTimes.samPrepared == null || !stepTimes.samPrepared"
                                    class="w-5 h-5 border-4 border-gray-300 rounded-full animate-spin"></div>
                                <div v-else class="text-green-500 font-bold">✔</div>

                                <span
                                class="font-medium"
                                :class="stepTimes.samPrepared ? 'text-green-600' : 'text-gray-800'"
                                >
                                Loading Segmentation Model
                                </span>
                            </div>
                            <div
                                class="text-sm"
                                :class="stepTimes.samPrepared ? 'text-green-600' : 'text-gray-600'"
                            >
                                {{ stepTimes.samPreparedStatus || 'pending' }}
                                <small v-if="stepTimes.samPrepared">
                                ({{ stepTimes.samPrepared }} ms)
                                </small>
                            </div>
                        </div>

                    </div>
                </div>

            </div>

        </div>

    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import Slider from '@vueform/slider';

import * as rosepetalModel from "rosepetal-model";

import config from '@/etc/rosepetal.json';

const $model = rosepetalModel.default.model;
const $dataset = rosepetalModel.default.dataset;

const emit = defineEmits([
    'setupTool',
    'setPredictedMask',
    'updateBrushSize',
    'openCreateTag',
    'tagSelected',
    'singleImage-VLM-Infere',
    'next-image',
    'VLMnoActive',
    'showSamHelp'
]);

const props = defineProps({
    layout: {
        type: Object,
        required: true
    },
    tagCounts: {
        type: Object,
        required: true
    },
    enableSAM: {
        type: Boolean,
        default: true
    }, 
    enableVLM: {
        type: Boolean,
        default: true
    },
    processing: {
        type: Boolean,
        default: false
    },
    VLMActive: {
        type: Boolean,
        default: false
    },
    SAMActive: {
        type: Boolean,
        default: false
    },
    newImage: {
        type: Boolean,
        default: false
    },
    stepTimes: {
        type: Object,
        default: () => ({})
    },
    samSteps: {
        type: Object,
        default: () => ({})
    }
});

const genericModels = computed(() =>
    models.value.filter(m =>
        Object.prototype.hasOwnProperty.call(m, 'finetuned') &&
        m.finetuned === false
    )
);

const finetunedModels = computed(() =>
    models.value.filter(m =>
        Object.prototype.hasOwnProperty.call(m, 'finetuned') &&
        m.finetuned === true
    )
);

const vlmOptions = computed(() => {
    const modelsToUse = vlmMode.value === 'generic' ? genericModels.value : finetunedModels.value;
    return modelsToUse;
});

const tagCounts = computed(() => {
    if (props.layout.dataset && localTagCounts.value) {
        const tags      = Object.values(props.layout.dataset.tags || {});
        const counts    = localTagCounts.value;

        let filteredTags;
        switch (props.layout.dataset.type) {
            case 'MULTICLASS':
            case 'ANOMALY':
                filteredTags = tags;
                break;
            case 'MULTILABEL':
                filteredTags = tags.filter(tag => !tag?.normal && tag.id !== '0');
                break;
            case 'imageObjectDetection':
                filteredTags = tags.filter(tag => !tag?.unclassified);
                break;
        }

        if (!props.layout.status.currentTag) {
            props.layout.status.currentTag = filteredTags[0];
        }

        const result = filteredTags.map(tag => ({
            ...tag,
            count: counts[tag.id] || 0
        }));

        return result;
    }

    return [];
});

const showAvailableTags = computed(() => {
    if (props.layout.dataset.type === 'MULTILABEL' || props.layout.dataset.type === 'imageObjectDetection') {
        if (currentTool.value === 'brush' || currentTool.value === 'polygon' || currentTool.value === 'drawBB' || currentTool.value === 'samDetection') {
            return true;
        }
    }
    return false;
});

const isPanelVisible = computed(() => {
    if (!props.SAMActive) { 
        return true;
    }
    return showSamPanel.value;
});


const currentTool = ref(false);

const isExpanded            = ref(false);
const localLayout           = ref(JSON.parse(JSON.stringify(props.layout)));
const localTagCounts        = ref({});

const availableTagsPanel    = ref(null);
const vlmPanel              = ref(null);

const dragOffset = ref({ x: 0, y: 0 });

const availableTagsPosition = ref({ left: '10px', top: '200px' });
const vlmPanelPosition      = ref({ left: '20px', top: '35px' });

const currentDraggingPanel = ref(null);

const editingIndex  = ref(null);
const showSamHelp   = ref(false);
const autoToggle    = ref(false);
const showSamPanel  = ref(false);
const samPanelHideTimer = ref(null);

const models        = ref([]);
const vlmMode       = ref('generic');   // Valor inicial: 'VLM Generic'
const vlmOption     = ref(null);        // Valor inicial según modo Generic


// ---- New Variables for Custom Prompt/Tag Section ----

const customPrompts = ref([]);
const showNewPrompt = ref(false);
const newPromptText = ref('');
const newTag        = ref('');

const onEditTagChange = (index) => {
  const tagObj = customPrompts.value[index].tag
  if (tagObj && tagObj.name) {
    customPrompts.value[index].prompt = tagObj.name.toLowerCase()
  }
}

const onNewTagChange = () => {
  if (newTag.value) {
    newPromptText.value = newTag.value.toLowerCase()
  }
}

const toggleNewPrompt = () => {
    showNewPrompt.value = !showNewPrompt.value;
    if (!showNewPrompt.value) {
        newPromptText.value = '';
        newTag.value = '';
    }
};

const applyNewPrompt = async () => {
    if (newPromptText.value && newTag.value) {
        const selectedTag = tagCounts.value.find(tag => tag.name === newTag.value);
        if (!selectedTag) {
            console.warn('El tag seleccionado no existe.');
            return;
        }
        // Guardamos el prompt junto con el objeto completo del tag
        customPrompts.value.push({
            prompt: newPromptText.value,
            tag: selectedTag
        });
        // Colapsar el panel y limpiar los inputs
        showNewPrompt.value = false;
        newPromptText.value = '';
        newTag.value = '';
    } else {
        console.warn('Se deben proporcionar tanto el prompt como el tag.');
    }
    await savePromptsToFirebase();
};

const expandPrompt = (index) => {
    editingIndex.value = index;
};

const applyEdit = async (index) => {
    editingIndex.value = null;
    await savePromptsToFirebase();
};

const deletePrompt = async (index) => {
    customPrompts.value.splice(index, 1);
    if (editingIndex.value === index) {
        editingIndex.value = null;
    }
    await savePromptsToFirebase();
};

const emitVlmData = () => {
    const data = {
        vlmOptions: vlmOption.value.uri, 
        tags: tagCounts.value,      
        prompts: customPrompts.value   
    };
    emit('singleImage-VLM-Infere', data);
};

const savePromptsToFirebase = async () => {
  const toSave = customPrompts.value.map(i => ({ tag: i.tag.id, prompt: i.prompt }));
  try {
    await $dataset.addPrompts(localLayout.value.dataset.id, toSave);
  } catch (err) {
    console.error('Error guardando prompts:', err);
  }
}


// ----- HANDLERS ----- //

const handleBrushSizeChange = (value) => {
    props.layout.canvas.tools.modalBrush.size = value;
    emit('updateBrushSize');
}

const setupTool = (tool) => {
    currentTool.value = tool;

    switch(tool) {
        case 'panning':
            props.layout.canvas.drawing = false;
            break;
        case 'brush':
            props.layout.canvas.drawing = true;
            break;
        case 'eraser':
            props.layout.canvas.drawing = true;
            break;
        case 'moveBB':
            props.layout.canvas.drawing = true;
            break;
        case 'drawBB':
            props.layout.canvas.drawing = true;
            break;
        case 'polygon':
            props.layout.canvas.drawing = true;
            break;
        case 'samDetection':
            props.layout.canvas.drawing = true;
            break;
        case 'vlmDetection':
            props.layout.canvas.drawing = false;
            break;
        default:
            props.layout.canvas.drawing = false;
    }

    emit('setupTool', tool);
}

const positionTagsPanelNearToolbar = () => {
    const toolbarEl = document.querySelector('.editor-toolbar');
    if (toolbarEl && availableTagsPanel.value) {
        const rect = toolbarEl.getBoundingClientRect();
        availableTagsPosition.value = {
            left: `${rect.left}px`,
            top: `${rect.bottom + 20}px`
        };
    }    
}


const startDragging = (panelType, event) => {
    let panelEl = null;

    if (panelType === 'tags') {
        panelEl = availableTagsPanel.value;
    } else if (panelType === 'vlm') {
        panelEl = vlmPanel.value;
    }
    if (panelEl && event.target.closest('.panel-header')) {
        currentDraggingPanel.value = panelType;
        const rect = panelEl.getBoundingClientRect();
        dragOffset.value = {
            x: event.clientX - rect.left,
            y: event.clientY - rect.top
        };
        event.preventDefault();
    }
}

const onMouseMove = (event) => {
    if (currentDraggingPanel.value === 'tags' && availableTagsPanel.value) {
        availableTagsPosition.value = {
            left: `${event.clientX - dragOffset.value.x}px`,
            top: `${event.clientY - dragOffset.value.y}px`
        };
    } else if (currentDraggingPanel.value === 'vlm' && vlmPanel.value) {
        vlmPanelPosition.value = {
            left: `${event.clientX - dragOffset.value.x}px`,
            top: `${event.clientY - dragOffset.value.y}px`
        };
    }
}

const onMouseUp = () => {
    currentDraggingPanel.value = null;
}

const handleSamMouseEnter = () => {
    if (samPanelHideTimer.value) {
        clearTimeout(samPanelHideTimer.value);
    }
    showSamPanel.value = true;
};

const handleSamMouseLeave = () => {
    if (samPanelHideTimer.value) {
        clearTimeout(samPanelHideTimer.value);
    }
    samPanelHideTimer.value = setTimeout(() => {
        showSamPanel.value = false;
    }, 2000);
};

const isTagActive = (tag) => {
    const datasetType = props.layout.dataset.type;
    if (datasetType === 'MULTICLASS' || datasetType === 'ANOMALY') {
        const currentTagId = props.layout.status.currentTag.id;
        return currentTagId === tag.id;
    } else if (datasetType === 'MULTILABEL') {
        return props.layout.status.currentTag.id === tag.id;
    } else if (datasetType === 'imageObjectDetection') {
        return props.layout.status.currentTag?.id === tag.id;
    }
    return false;
}

const selectTag = (tag = false) => {
    props.layout.status.currentTag = tag || tagCounts.value[0];
    
    if (props.layout.dataset.type === 'MULTILABEL') {
        if (props.layout.status.multilabelTag?.id == '0') {
            props.layout.status.multilabelTag = props.layout.dataset.tags['0'] || { id: '0' };
        } else if (tag.id !== 'OK') {
            props.layout.status.multilabelTag = tag;
        }
        
        if (props.layout.selectedCanvas) {
            props.layout.selectedCanvas.name    = tag.id;
            props.layout.selectedCanvas.stroke  = tag.color;
            emit('updateCanvas', tag);
        }
    }

    emit('tagSelected');
}


onMounted(async () => {
    switch (props.layout.dataset.type) {
        case 'MULTICLASS':
            currentTool.value = 'panning';
            break;
        case 'MULTILABEL':
            currentTool.value = 'brush';
            break;
        case 'imageObjectDetection':
            currentTool.value = 'drawBB';
            break;
    }

    await nextTick();

    positionTagsPanelNearToolbar();
    
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    if (props.SAMActive) {
        setupTool('panning');
    }
});


onUnmounted(() => {
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
});

watch(() => props.layout, (newVal) => {
    localLayout.value = JSON.parse(JSON.stringify(newVal));
}, { deep: true });

watch(() => props.tagCounts, (newVal) => {
    localTagCounts.value = newVal;
}, { deep: true });

watch(() => props.layout.canvas.tools.currentTool, (newVal) => {
    currentTool.value = newVal;
}, { deep: true });


watch (vlmOption, (newValue) => {
    emit('VLMnoActive');
});

watch(autoToggle, (newValue) => {
    if (newValue && props.enableVLM) {
        emitVlmData(); 
    }
});

watch(() => props.processing, async (newValue) => {
    if (!newValue && autoToggle.value && props.enableVLM) {
        await new Promise(resolve => setTimeout(resolve, 500));
        emit('next-image');
    }
});

watch(() => props.newImage, (newValue) => {
    if (newValue && autoToggle.value) {
        emitVlmData();
    }
});

watch(currentTool, async (tool) => {
    if (tool === 'vlmDetection') {
        try {
            // const orch_health = await $vlm.checkOrchestratorHealth(config.firebaseConfig.projectId);
            // console.log('Orchestrator health:', orch_health);
            // console.log('Cargando modelos VLM...');
            // const resppp = await $vlm.checkFlorence2health(config.firebaseConfig.projectId);
            // console.log('Florence2health response:', resppp);
            // 1) GET VLM MODELS
            const allModels = await $model.list_no_constraints();
            const filteredModels = allModels.filter(m => Object.prototype.hasOwnProperty.call(m, 'finetuned'));
            models.value = filteredModels;
            const firstOptions = vlmOptions.value;
            vlmOption.value = firstOptions.length ? firstOptions[0] : null;


            // 2) GET PROMPTS
            const resp = await $dataset.getPrompts(localLayout.value.dataset.id);
            let loaded = [];
            if (resp.status === 'success' && Array.isArray(resp.prompts)) {
                // 3) Filtra los que todavía existan en tagCounts
                const existingTagIds = tagCounts.value.map(t => t.id);
                loaded = resp.prompts
                .filter(p => existingTagIds.includes(p.tag))
                .map(p => {
                    // Reconstruye el objeto con el tag completo
                    const tagObj = tagCounts.value.find(t => t.id === p.tag);
                    return { tag: tagObj, prompt: p.prompt };
                });
            }
            customPrompts.value = loaded;



        } catch (err) {
            console.error('Error cargando modelos VLM:', err);
        }
    }
});

defineExpose({
    selectTag
});
</script>

<style scoped>
/* BASE STYLES */
:root {
    --primary-50: #edf2fa;
    --primary-100: #d2dff0;
    --primary-200: #a7bfe4;
    --primary-300: #7c9fd7;
    --primary-400: #517fc9;
    --primary-500: #3664b3;
    --primary-600: #1e4591;
    --primary-700: #17356e;
    --primary-800: #10254c;
    --primary-900: #081529;
}

.editor-container {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    overflow: visible !important;
}

/* TOOLBAR */
.editor-toolbar {
    position: fixed;
    left: 20px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    overflow: visible !important;
}

.toolbar-main {
    background: #fff;
    border-radius: 8px;
    padding: 0.4rem 0.2rem 0.2rem 0.2rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border: 1px solid #e2e8f0;
    display: flex;
    flex-direction: column;
    align-items: center;
    max-height: 80vh;
    overflow-y: visible;
    overflow-x: visible;
}

.toolbar-buttons {
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: center;
    overflow: visible !important;
    width: 100%;
}

.toolbar-btn {
    padding: 8px;
    border-radius: 6px;
    color: #64748b;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background-color: #f9fafb;
    border: 1px solid transparent;
}

.toolbar-btn:hover {
    background: #f1f5f9;
    color: #0f172a;
    border-color: #e2e8f0;
}

.toolbar-btn.active {
    background: var(--primary-50);
    color: var(--primary-600);
    border-color: var(--primary-200);
}


/* NEW DRAGGABLE TAGS PANEL */
.draggable-tags-panel {
    position: fixed;
    z-index: 1100;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
    border: 1px solid #e2e8f0;
    width: 180px;
    user-select: none;
    display: flex;
    flex-direction: column;
    max-height: 30vh;
    overflow: hidden;
}

.draggable-tags-panel .panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;
    background-color: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    cursor: move;
}

.panel-title {
    font-size: 14px;
    font-weight: 500;
    color: #4a5568;
    margin: 0;
}

.drag-handle {
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 4px;
}

.drag-handle:hover {
    background-color: #f1f5f9;
}

.add-tag-section {
    padding: 8px 12px;
    border-bottom: 1px solid #e2e8f0;
}

.add-tag-section .action-button {
    display: inline-flex;
    align-items: center;
    width: 100%;
    justify-content: center;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 14px;
    transition: all 0.2s ease;
}

.add-tag-section .action-button.primary {
    background-color: var(--primary-50, #edf2fa);
    color: var(--primary-600);
    border: 1px solid var(--primary-200);
}

.add-tag-section .action-button.primary:hover {
    background-color: var(--primary-100);
}

.tags-list {
    overflow-y: auto;
    padding: 6px;
    flex-grow: 1;
    max-height: 400px;
}

.tag-item {
    display: flex;
    align-items: center;
    padding: 6px 10px;
    border-radius: 6px;
    margin-bottom: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.tag-item:hover {
    background-color: #f1f5f9;
}

.tag-item.active {
    background-color: var(--primary-50);
}

.tag-color {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    margin-right: 10px;
    flex-shrink: 0;
    border: 1px solid rgba(0, 0, 0, 0.1);
}

.tag-name {
    font-size: 14px;
    color: #4b5563;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.tag-item.active .tag-name {
    color: var(--primary-600);
    font-weight: 500;
}

.tag-count {
    font-size: 12px;
    color: #64748b;
    background: #f1f5f9;
    padding: 2px 6px;
    border-radius: 12px;
    margin-left: auto;
}

.vlm-panel {
    /* Para que el panel sea más alto que 30vh */
    max-height: 70vh;
    overflow-y: auto;
}

.custom-prompt-tag {
    font-size: 14px;
    font-weight: bold;
    color: var(--primary-600);
    margin-right: 4px;
}

.custom-prompt-text {
    font-size: 13px;
    color: #4b5563;
}

/* Estilos para el slide toggle "switch" */
.switch {
    position: relative;
    display: inline-block;
    width: 50px;
    height: 28px;
}

.switch input {
    opacity: 0;
    width: 0;
    height: 0;
}

.slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: 0.4s;
    border-radius: 34px;
}

.slider:before {
    position: absolute;
    content: "";
    height: 22px;
    width: 22px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.4s;
    border-radius: 50%;
}

.switch input:checked+.slider {
    background-color: #1e4591;
}

.switch input:checked+.slider:before {
    transform: translateX(22px);
}


.tag-item.active .tag-count {
    background: var(--primary-100);
    color: var(--primary-600);
}

.no-tags-message {
    text-align: center;
    color: #94a3b8;
    padding: 20px 0;
}

/* VLM disabled mode */

.disabled-section :is(button,
                       select,
                       textarea,
                       input):not([type="checkbox"]) {
  pointer-events: none;   
  opacity: 0.5;           
}


.disabled-section .switch,
.disabled-section .switch * {
  pointer-events: auto;
  opacity: 1;
}



/* TOOL PANEL */
.tool-panel {
    position: absolute;
    left: 50px;
    top: auto;
    bottom: 0;
    transform: none;
    background: #fff;
    border-radius: 8px;
    padding: 0.6rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    border: 1px solid #e2e8f0;
    width: 150px;
    z-index: 9999;
}


/* PANEL HEADERS */
.panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0;
}


/* ACTION BUTTONS */
.action-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: normal;
    transition: all 0.2s ease;
    border: 1px solid transparent;
    white-space: nowrap;
}

.action-button.danger {
    background-color: #fee2e2;
    color: #dc2626;
    border-color: #fecaca;
}

.action-button.danger:hover {
    background-color: #fecaca;
}


/* LABEL ACTIONS */
.label-actions {
    display: flex;
    gap: 8px;
    margin-top: 16px;
}


/* TOOLBAR PANEL */
.toolbar-panel {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border: 1px solid #e2e8f0;
    margin-left: 8px;
}

.panel-content {
    padding: 16px;
}

.panel-section {
    margin-bottom: 16px;
}

.panel-section:last-child {
    margin-bottom: 0;
}

.relative {
    position: relative;
    overflow: visible !important;
}

.tags-list::-webkit-scrollbar {
    width: 4px;
}

.tags-list::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 10px;
}

.tags-list::-webkit-scrollbar-thumb {
    background: #94a3b8;
    border-radius: 10px;
}

.tags-list::-webkit-scrollbar-thumb:hover {
    background: #64748b;
}

.filters-popup {
    z-index: 1500;
}

/* SAM */
.sam-tools-container {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
    width: 100%;
    padding: 0.2rem 0.2rem;
    box-sizing: border-box;
}

.sam-help-btn {
    padding: 3px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-600);
    background-color: transparent;
    transition: all 0.2s;
}

.sam-help-btn:hover {
    background-color: var(--primary-50);
}

.sam-tools-container.sam-active {
    box-shadow: inset 0 0 0 1px var(--primary-300);
    border-radius: 6px;
}

.sam-panel {
    width: max-content;
    position: absolute;
    left: 50px;
    top: auto;
    bottom: 0;
    transform: none;
    padding: 0.6rem;
    margin-left: 0.2rem;
    z-index: 9999;
}

.sam-panel.sam-loading {
    width: 320px;
}

.sam-instruction-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 14px;
    color: #374151;
    margin-bottom: 10px;
    margin-top: 10px;
    padding: 0 4px;
    gap: 32px;
}

.instruction-text {
    flex: 1;
    line-height: 1.4;
    font-weight: normal;
}

.instruction-keys {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}

.or-text {
    font-size: 12px;
    color: #6b7280;
    margin: 0 4px;
    font-weight: 500;
}

.plus-text {
    font-size: 12px;
    color: #6b7280;
    margin: 0 4px;
    font-weight: normal;
}

.drag-text {
    font-size: 12px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-weight: normal;
    color: #374151;
    background-color: #f3f4f6;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    padding: 2px 6px;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.1);
}

.inline-icon {
    color: var(--primary-600);
    vertical-align: middle;
}

.sam-divider {
    height: 1px;
    background-color: #e5e7eb;
    margin: 8px 0;
}

.sam-instruction:first-child {
    font-weight: 600;
    color: var(--primary-600);
}

kbd {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    padding: 2px 6px;
    margin: 2px 0;
    font-size: 12px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-weight: normal;
    color: #374151;
    background-color: #f3f4f6;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.1);
}
</style>